import { useDeferredValue, useEffect, useState } from "react"

import { load } from "scrivito"
import {
  BuildingReferenceObj,
  BuildingReferenceObjClass
} from "scrivito/Objs/BuildingReference/BuildingReferenceObjClass"
import {
  ProductObj,
  ProductObjClass
} from "scrivito/Objs/Product/ProductObjClass"
import { ProductBaseVariantObj } from "scrivito/Objs/ProductBaseVariant/ProductBaseVariantObjClass"
import { getCurrentSiteID } from "shared/lib/getCurrentSiteID"

import { fetchBuildingReferencesByInstallationReferences } from "../api/fetchBuildingReferencesByInstallationReferences"
import { fetchInstallationReferencesByProducts } from "../api/fetchInstallationReferencesByProducts"
import { BuildingReferenceFilterFormData } from "../types/BuildingReferenceFilterFormData"

async function fetchProducts(productTypes: string[] | null | undefined) {
  if (!productTypes || productTypes.length === 0) {
    return null
  }

  const products = await load(() =>
    ProductObjClass.onSite(getCurrentSiteID())
      .where("productType", "equals", productTypes)
      .toArray()
  )

  const productBaseVariants = await Promise.all(
    products.map(product => load(() => product.children()))
  ).then(children => children.flatMap(c => c))

  return [...products, ...productBaseVariants] as (
    | ProductObj
    | ProductBaseVariantObj
  )[]
}

export async function fetchBuildingReferencesWithFilter(
  filterState: BuildingReferenceFilterFormData
) {
  const products = await fetchProducts(filterState.products || [])

  const installationRefs = products
    ? await fetchInstallationReferencesByProducts(products)
    : null

  const buildingTypes = filterState.buildingTypes?.length
    ? filterState.buildingTypes
    : null
  const searchTerm = filterState.searchTerm || null

  const baseQuery = BuildingReferenceObjClass

  if (
    filterState.products?.length &&
    (!installationRefs || installationRefs.length === 0)
  ) {
    return []
  }

  if (installationRefs && installationRefs.length > 0) {
    const parentBuildingRefs =
      await fetchBuildingReferencesByInstallationReferences(installationRefs)

    if (parentBuildingRefs.length === 0) {
      return []
    }

    let query = baseQuery.where("_id", "equals", parentBuildingRefs)

    if (buildingTypes) {
      query = query.and("buildingType", "contains", buildingTypes)
    }

    if (searchTerm) {
      query = query.and("title", "matches", searchTerm)
    }

    return load(() => query.toArray())
  }

  if (buildingTypes) {
    let query = baseQuery.where("buildingType", "contains", buildingTypes)

    if (searchTerm) {
      query = query.and("title", "matches", searchTerm)
    }

    return load(() => query.toArray())
  }

  if (searchTerm) {
    return load(() => baseQuery.where("title", "matches", searchTerm).toArray())
  }

  return load(() => baseQuery.all().toArray())
}

export function useBuildingReferenceFilter(
  filterState: BuildingReferenceFilterFormData
): {
  buildingReferenceObjs: BuildingReferenceObj[]
  isLoading: boolean
} {
  const [isLoading, setIsLoading] = useState(true)
  const [buildingReferences, setBuildingReferences] = useState<
    BuildingReferenceObj[]
  >([])
  const deferredBuildingReferences = useDeferredValue(buildingReferences)

  useEffect(() => {
    let isCancelled = false
    setIsLoading(true)

    fetchBuildingReferencesWithFilter(filterState)
      .then(results => {
        if (!isCancelled) {
          setBuildingReferences(results)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setBuildingReferences([])
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [filterState])

  return { buildingReferenceObjs: deferredBuildingReferences, isLoading }
}
